import React, { useEffect, useRef, useState } from "react";
import { Search, X } from "lucide-react";
import { api } from "../lib/api";

export const MovieSearch = ({ onSelect, onClear, disabled }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const timer = useRef(null);
  const skip = useRef(false); // skip the search that a programmatic setQ (pick) would trigger

  useEffect(() => {
    function onClickOutside(e) {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  useEffect(() => {
    if (skip.current) {
      skip.current = false;
      return;
    }
    if (timer.current) clearTimeout(timer.current);
    if (!q.trim()) {
      setResults([]);
      setOpen(false);
      return;
    }
    setLoading(true);
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/movies/search`, { params: { q } });
        setResults(data);
        setOpen(true);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => timer.current && clearTimeout(timer.current);
  }, [q]);

  const pick = (m) => {
    if (timer.current) clearTimeout(timer.current);
    skip.current = true; // prevent the setQ below from re-opening the dropdown
    onSelect(m);
    setQ(`${m.title} — ${m.year}`);
    setResults([]);
    setOpen(false);
  };

  const clear = () => {
    if (timer.current) clearTimeout(timer.current);
    setQ("");
    setResults([]);
    setOpen(false);
    onClear && onClear();
  };

  return (
    <div className="relative w-full" ref={boxRef}>
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-amber-400/70 pointer-events-none" />
        <input
          data-testid="movie-search-input"
          disabled={disabled}
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            onClear && onClear(); // typing invalidates any previously selected movie
          }}
          onFocus={() => results.length && setOpen(true)}
          placeholder="Search for a movie..."
          className="w-full pl-12 pr-11 py-4 rounded-xl bg-black/50 border border-amber-500/25 text-base sm:text-lg text-zinc-100 placeholder:text-zinc-500 focus:outline-none focus:border-amber-400/70 focus:ring-2 focus:ring-amber-500/20 transition-all disabled:opacity-40"
        />
        {q && !disabled && (
          <button
            onClick={clear}
            data-testid="search-clear-button"
            className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-amber-300"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>
      {open && !disabled && (
        <div
          data-testid="search-results-dropdown"
          className="absolute z-40 mt-2 w-full max-h-72 overflow-auto ac-glass rounded-xl border border-amber-500/25 shadow-2xl"
        >
          {loading && <div className="px-4 py-3 text-sm text-zinc-400">Searching…</div>}
          {!loading && results.length === 0 && (
            <div className="px-4 py-3 text-sm text-zinc-400">No movies found.</div>
          )}
          {results.map((m, i) => (
            <button
              key={m.id}
              data-testid={`search-result-${i}`}
              onClick={() => pick(m)}
              className="w-full text-left px-4 py-3 flex items-center justify-between hover:bg-amber-500/10 transition-colors border-b border-white/5 last:border-0"
            >
              <span className="text-zinc-100 font-medium">{m.title}</span>
              <span className="text-amber-400/80 text-sm font-mono-ac">{m.year}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
